import { Fragment, type FunctionComponent } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { useSelector } from "react-redux";
import { State } from "@App/state";
import { useTranslation } from "react-i18next";
import { SortOrderType } from "@App/types/enums";

export type SortListBoxProps = {
  selected: string;
  displayReviewFilter?: boolean;
  onChange: (sortBy: SortOrderType) => void;
};

export const SortListBox: FunctionComponent<SortListBoxProps> = (props: SortListBoxProps) => {
  const { t } = useTranslation();
  const organization = useSelector((state: State) => state.orders.organization);

  const sortings = (): string[] => {
    if (props.displayReviewFilter) return organization.settings?.garage_list.sorting ?? [];
    return (
      organization.settings?.garage_list?.sorting?.filter((sort) => sort !== "best_reviewed") ?? []
    );
  };

  return (
    <Listbox
      value={props.selected}
      onChange={(value: string) => props.onChange(value as SortOrderType)}
    >
      <div className="relative mt-1">
        <ListboxButton className="relative w-full py-2 pl-3 pr-10 text-left bg-white rounded shadow cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-primary focus-visible:ring-offset-2 focus-visible:border-primary">
          <span className="block truncate">{t("garageList.sorting." + props.selected)}</span>
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronUpDownIcon className="w-5 h-5 text-primary" aria-hidden="true" />
          </span>
        </ListboxButton>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <ListboxOptions className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded shadow max-h-60 ring-1 ring-opacity-20 ring-neutral-100 z-20 focus:outline-none sm:text-sm">
            {sortings().map((sortBy, sortByIdx) => (
              <ListboxOption
                key={sortBy + sortByIdx}
                className={({ focus }) =>
                  `cursor-default select-none relative py-2 pl-10 pr-4 ${
                    focus ? "text-white bg-primary" : "text-neutral-700"
                  }`
                }
                value={sortBy}
              >
                {props.selected && (
                  <>
                    <span
                      className={`block truncate ${props.selected ? "font-medium" : "font-normal"}`}
                    >
                      {t("garageList.sorting." + sortBy)}
                    </span>
                    {props.selected === sortBy ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                        <CheckIcon className="w-5 h-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  );
};
